@use '@/styles/tokens' as *;

.wrap {
  background: var(--surface-card);
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin-bottom: var(--space-3);
}

.right {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.bulkBar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  margin-bottom: var(--space-3);
  background: var(--surface-sunken);
  border-radius: var(--radius-sm);
}

.tableCard {
  // Scroll horizontally when columns overflow (e.g. Orders' 9 columns) instead of
  // clipping them; the single table scrolls so header and body stay aligned. Vertical
  // stays clipped (rows are paginated) to preserve the rounded corners.
  overflow-x: auto;
  overflow-y: clip;
  border: 1px solid var(--overlay-10);
  border-radius: $radius-lg;
}

.pagination {
  margin-top: var(--space-4);
}

.table {
  // rc-table's inline `min-width: 100%` on every internal <table> (Table.js / FixedHolder,
  // whenever `scroll.x` is set) is deliberately left alone — see ADR-0032's 2026-09-22 amendment.
  // It was overridden to 0 so a table would render at exactly its declared column widths; what
  // that actually buys is a table narrower than its own card, with the header band, the row
  // separators and the hover highlight all stopping short of the card's border (ADO 119717, QA
  // 2026-09-22). The blow-out bug ADR-0032 exists to fix is held by `scroll={{ x: totalWidth }}`
  // plus `tableLayout="fixed"` (decision 5), which pin the table at its declared sum and stop any
  // cell's content expanding it; `min-width` only raises the floor to the container, and under
  // fixed layout the surplus distributes proportionally, so Figma's column *ratios* survive.
  // Only a table whose declared sum is narrower than its card is affected at all.

  :global(.ant-table-thead > tr > th) {
    height: 40px;
    padding-top: 0;
    padding-bottom: 0;
    font-size: 12px;
    font-weight: var(--font-weight-regular);
    white-space: nowrap;
  }

  :global(.ant-table-tbody > tr > td) {
    height: 46px;
    padding-top: 0;
    padding-bottom: 0;
    border-bottom: 0.5px solid var(--border-subtle);
    white-space: nowrap;
  }

  // The default-width action column only fits its 28px kebab once antd's 20px
  // `cellPaddingInline` is tightened (see ACTION_COLUMN_WIDTH).
  :global(.ant-table-thead > tr > th).actionCellCompact,
  :global(.ant-table-tbody > tr > td).actionCellCompact {
    padding-inline: 10px;
    text-align: center;
  }

  // The floating action column: a 1px sticky track whose ellipsis is positioned out of it, over
  // the right edge of the row (Gmail-style). The track itself shows nothing — no padding, and no
  // scroll-edge shadow, since there is no frozen strip for content to scroll under.
  :global(.ant-table-thead > tr > th).floatingActionCell,
  :global(.ant-table-tbody > tr > td).floatingActionCell {
    padding: 0;
    overflow: visible;

    &::after {
      display: none;
    }
  }

  // The sticky action column's edge. antd's default (a 30px, 8px-blur `colorSplit` wash) read as
  // a wall beside a column that is empty at rest; kept as the "more content scrolls under here"
  // cue it is, only fainter and narrower. antd shows it only while content is actually hidden.
  :global(.ant-table-cell-fix-end-shadow-show::after) {
    width: 12px;
    box-shadow: inset -6px 0 6px -6px var(--overlay-10);
  }
}

.sortIcon {
  color: var(--text-tertiary);
  vertical-align: -2px;
}

.sortIconActive {
  color: var(--text-primary);
  vertical-align: -2px;
}

// A row's ellipsis is a hover affordance: hidden at rest, revealed on row hover. Figma
// 30:434753 ("Action Column in Tables") states it for every table listing module, which is why
// it lives here rather than in a page — two pages had each written their own copy against their
// own story's AC1, and seven other tables shipped without it (ADO 119717).
//
// `opacity`, not `visibility`/`display`, so the trigger stays in the tab order; `:focus-within`
// then keeps it lit both while it is tabbed to and while its own menu is open (antd leaves focus
// on the trigger and sets no `aria-expanded`, so focus is the only usable signal).
//
// `hover: hover` guards the whole rule: with no hover capability there is no gesture that could
// reveal it, and ADR-0006 puts Dealer/Field Installer on phones against these same listings.
// The floating ellipsis is anchored to the sticky track's right edge and extends leftwards over
// the row. Its fade matches the row's hover colour (antd `rowHoverBg`, neutral-50), so the cell
// text it overlaps dissolves under it instead of being cut; at rest (and on a device with no
// hover, where it is always shown) it fades to the card.
//
// Only the controls take the pointer. The box sits over the last visible cell of every row, even
// at rest while transparent, so were it hit-testable it would swallow clicks and hovers on that
// cell — the fault the old frozen strip had over the Selected Device column (ADO 119718).
.tableCard [data-dt-floating-action] {
  pointer-events: none;
  position: absolute;
  inset-block: 0;
  inset-inline-end: 0;
  display: flex;
  align-items: center;
  padding-inline: var(--space-6) 10px;
  background: linear-gradient(to right, transparent, var(--surface-card) var(--space-5));

  > * {
    pointer-events: auto;
  }
}

.tableCard :global(.ant-table-row):hover [data-dt-floating-action] {
  background: linear-gradient(to right, transparent, $color-neutral-50 var(--space-5));
}

@media (hover: hover) {
  .tableCard :global(.ant-table-row) [data-dt-action-cell][data-dt-reveal-on-hover] {
    opacity: 0;
    transition: opacity 120ms ease;
  }

  .tableCard :global(.ant-table-row):hover [data-dt-action-cell][data-dt-reveal-on-hover],
  .tableCard :global(.ant-table-row) [data-dt-action-cell][data-dt-reveal-on-hover]:focus-within {
    opacity: 1;
  }
}

// A row `isRowClickable` rejects offers nothing to hover for, so it keeps its resting surface
// rather than antd's `rowHoverBg` (Customer Devices tab: Pending rows, ADO 122607). antd 6 paints
// that hover through a class rc-table adds to each cell on mouse-enter, not through `:hover`
// (`.ant-table-tbody .ant-table-row > .ant-table-cell-row-hover`, its hash in a zero-specificity
// `:where`), so this targets the same class and outranks it by the attribute.
.tableCard
  :global(.ant-table-tbody > tr.ant-table-row[data-dt-inert] > td.ant-table-cell-row-hover) {
  background: var(--surface-card);
}
